import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Check } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { Em, Headline, Reveal, Section, Subhead } from "../components/marketing/primitives";
import { API_BASE_URL } from "../api/client";

/**
 * Quotation / inquiry form (September 2026). Captures what sales needs to
 * price a package or service: who, where, how big the estate is, what they
 * want, how often, and how they prefer to pay. Posts to /v1/inquiries; the
 * API stores every submission and notifies sales.
 *
 * The option lists are fetched from the API so the form and the validation
 * can never disagree; a built-in copy renders until they arrive.
 */

type Option = { id: string; label: string };

const FALLBACK_INTERESTS: Option[] = [
  { id: "trial", label: "30-day guided trial of the platform" },
  { id: "assessment", label: "One-off code security risk assessment ($1,500 per project)" },
  { id: "code-trust", label: "Code Trust assessment (NS-CTAF)" },
  { id: "third-party-assurance", label: "Third-party software assurance (from $5,000)" },
  { id: "managed-assessment", label: "Managed code risk assessment service (from $2,500)" },
  { id: "starter", label: "Starter package ($199 a month)" },
  { id: "growth", label: "Growth package ($499 a month)" },
  { id: "enterprise", label: "Large Enterprise package ($999 a month)" },
  { id: "community", label: "Community (free)" },
  { id: "other", label: "Not sure yet, please advise" },
];
const FALLBACK_FREQUENCIES: Option[] = [
  { id: "once", label: "Once" },
  { id: "monthly", label: "Monthly" },
  { id: "quarterly", label: "Quarterly" },
  { id: "annual", label: "Annual" },
  { id: "continuous", label: "Continuous (every change)" },
  { id: "unsure", label: "Not sure yet" },
];
const FALLBACK_PAYMENT: Option[] = [
  { id: "card", label: "Card (secure checkout)" },
  { id: "invoice", label: "Invoice and bank transfer" },
  { id: "unsure", label: "Not sure yet" },
];

const FIELD =
  "w-full rounded-xl border border-[#E4DFD3] bg-white px-4 py-3 text-sm text-[#17150F] outline-none transition-colors placeholder:opacity-40 focus:border-[#1E4D38] focus:ring-2 focus:ring-[#1E4D38]/15";
const LABEL = "block text-[11px] font-semibold uppercase tracking-[0.16em] opacity-60";

type FormState = {
  name: string;
  company: string;
  country: string;
  email: string;
  developers: string;
  products: string;
  interest: string;
  frequency: string;
  paymentType: string;
  summary: string;
  website: string; // honeypot, never shown
};

export default function Inquiry() {
  const location = useLocation();
  const preset = useMemo(() => new URLSearchParams(location.search).get("interest") || "", [location.search]);

  const [interests, setInterests] = useState<Option[]>(FALLBACK_INTERESTS);
  const [frequencies, setFrequencies] = useState<Option[]>(FALLBACK_FREQUENCIES);
  const [paymentTypes, setPaymentTypes] = useState<Option[]>(FALLBACK_PAYMENT);
  const [form, setForm] = useState<FormState>({
    name: "", company: "", country: "", email: "", developers: "", products: "",
    interest: FALLBACK_INTERESTS.some((o) => o.id === preset) ? preset : "",
    frequency: "", paymentType: "", summary: "", website: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "failed">("idle");
  const [reference, setReference] = useState("");
  const [failure, setFailure] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/v1/inquiries/options`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        if (Array.isArray(d.interests) && d.interests.length) setInterests(d.interests);
        if (Array.isArray(d.frequencies) && d.frequencies.length) setFrequencies(d.frequencies);
        if (Array.isArray(d.paymentTypes) && d.paymentTypes.length) setPaymentTypes(d.paymentTypes);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    document.title = "Request a quote | Paxley";
  }, []);

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => { const n = { ...er }; delete n[k]; return n; });
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("submitting");
    setFailure("");
    try {
      const res = await fetch(`${API_BASE_URL}/v1/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: preset ? `pricing:${preset}` : "inquiry-page" }),
      });
      if (res.status === 202) {
        const d = await res.json().catch(() => ({}));
        setReference(d.reference || "");
        setStatus("done");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (res.status === 400) {
        const d = await res.json().catch(() => ({}));
        setErrors(d.errors || {});
        setStatus("idle");
        return;
      }
      if (res.status === 429) {
        setFailure("Too many requests from this network. Please try again in a few minutes, or email yash.sancheti@paxleysoftware.com.");
      } else {
        setFailure("Something went wrong on our side. Please try again, or email yash.sancheti@paxleysoftware.com.");
      }
      setStatus("failed");
    } catch {
      setFailure("We could not reach the server. Please check your connection and try again.");
      setStatus("failed");
    }
  }

  const err = (k: string) => errors[k] ? <p className="mt-1.5 text-xs text-[#B91C1C]">{errors[k]}</p> : null;

  return (
    <MarketingLayout>
      <Section padding="hero" container="narrow">
        <div className="text-center">
          <Headline size="hero" className="mx-auto">
            Tell us about the code. <Em>We&rsquo;ll quote it.</Em>
          </Headline>
          <Subhead size="large" className="mx-auto mt-6 max-w-xl">
            Packages and services are priced by the size of the code, how often it is scanned, and
            the term. A few details and we reply within two business days.
          </Subhead>
        </div>
      </Section>

      <Section padding="tight" className="pb-24">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {status === "done" ? (
              <Reveal>
                <div className="rounded-2xl border border-[#1E4D38] bg-[#E4EFE4] p-8">
                  <h2 className="text-2xl font-semibold">Thank you. Your request is in.</h2>
                  {reference && (
                    <p className="mt-3 text-sm opacity-80">
                      Reference <span className="font-mono font-semibold">{reference}</span>. Keep it for your records.
                    </p>
                  )}
                  <p className="mt-4 text-sm leading-relaxed opacity-80">
                    Someone from the team will reply within two business days with a quotation, or with a
                    couple of questions about scope if we need them. If you would rather talk sooner, email{" "}
                    <a className="underline" href="mailto:yash.sancheti@paxleysoftware.com">yash.sancheti@paxleysoftware.com</a>.
                  </p>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <form onSubmit={submit} noValidate className="rounded-2xl border border-[#E4DFD3] bg-white p-6 sm:p-8">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className={LABEL} htmlFor="name">Name</label>
                      <input id="name" className={`${FIELD} mt-2`} value={form.name} onChange={set("name")} autoComplete="name" required />
                      {err("name")}
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="company">Company / organisation</label>
                      <input id="company" className={`${FIELD} mt-2`} value={form.company} onChange={set("company")} autoComplete="organization" required />
                      {err("company")}
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="country">Country</label>
                      <input id="country" className={`${FIELD} mt-2`} value={form.country} onChange={set("country")} autoComplete="country-name" required />
                      {err("country")}
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="email">Email address</label>
                      <input id="email" type="email" className={`${FIELD} mt-2`} value={form.email} onChange={set("email")} autoComplete="email" required />
                      {err("email")}
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="developers">Number of developers</label>
                      <input id="developers" type="number" min={0} step={1} inputMode="numeric" className={`${FIELD} mt-2`} value={form.developers} onChange={set("developers")} placeholder="e.g. 25" />
                      {err("developers")}
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="products">Number of products / source codes</label>
                      <input id="products" type="number" min={0} step={1} inputMode="numeric" className={`${FIELD} mt-2`} value={form.products} onChange={set("products")} placeholder="e.g. 4" />
                      {err("products")}
                    </div>
                    <div className="sm:col-span-2">
                      <label className={LABEL} htmlFor="interest">Software bundle or service of interest</label>
                      <select id="interest" className={`${FIELD} mt-2`} value={form.interest} onChange={set("interest")} required>
                        <option value="">Choose one</option>
                        {interests.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                      </select>
                      {err("interest")}
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="frequency">Scanning frequency / term</label>
                      <select id="frequency" className={`${FIELD} mt-2`} value={form.frequency} onChange={set("frequency")}>
                        <option value="">Choose one (optional)</option>
                        {frequencies.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                      </select>
                      {err("frequency")}
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="paymentType">Preferred payment type</label>
                      <select id="paymentType" className={`${FIELD} mt-2`} value={form.paymentType} onChange={set("paymentType")}>
                        <option value="">Choose one (optional)</option>
                        {paymentTypes.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                      </select>
                      {err("paymentType")}
                    </div>
                    <div className="sm:col-span-2">
                      <label className={LABEL} htmlFor="summary">Inquiry summary / comments</label>
                      <textarea id="summary" rows={5} className={`${FIELD} mt-2`} value={form.summary} onChange={set("summary")} placeholder="Languages, repositories, what you need the results for, and any deadline." />
                      {err("summary")}
                    </div>
                    <div className="hidden" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input id="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                    </div>
                  </div>

                  {failure && <p className="mt-5 rounded-xl border border-[#B91C1C]/30 bg-[#B91C1C]/5 px-4 py-3 text-sm text-[#B91C1C]">{failure}</p>}

                  <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#1E4D38] px-6 py-3 text-sm font-semibold text-[#F5F1E6] transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#173D2C] active:scale-[0.98] disabled:opacity-60"
                    >
                      {status === "submitting" ? "Sending" : "Request a quote"}
                    </button>
                    <p className="text-xs opacity-55">
                      We use these details only to prepare your quotation. See our{" "}
                      <a className="underline" href="/privacy">privacy policy</a>.
                    </p>
                  </div>
                </form>
              </Reveal>
            )}
          </div>

          <aside className="lg:col-span-5">
            <Reveal delay={0.08}>
              <div className="rounded-2xl bg-[#1E4D38] p-7 text-[#F5F1E6]">
                <h3 className="text-lg font-semibold">What happens next</h3>
                <ol className="mt-5 space-y-4 text-sm">
                  {[
                    ["1", "We read your request and check the scope: size of the code, scanning frequency, and the term you have in mind."],
                    ["2", "You get a quotation, or a short call if the scope needs a conversation. Services start from the prices shown on the pricing page and are confirmed against scope."],
                    ["3", "Once agreed, we set up access. Card payment goes through secure checkout; invoices and bank transfers are arranged with the quote."],
                  ].map(([n, t]) => (
                    <li key={n} className="flex gap-3 border-t border-white/15 pt-4">
                      <span className="font-mono text-xs opacity-60">{n}</span>
                      <span className="opacity-85">{t}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="mt-5 rounded-2xl border border-[#E4DFD3] bg-white p-7">
                <h3 className="text-base font-semibold">What we quote</h3>
                <ul className="mt-4 space-y-2.5 text-sm">
                  {[
                    "Third-party software assurance: scanning vendor software before acceptance and as it changes, from $5,000",
                    "Managed code risk assessment service: we run Paxley for you, from $2,500",
                    "One-off code security risk assessment: $1,500 per project",
                    "Self-hosted packages: Starter $199, Growth $499, Large Enterprise $999 a month",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4D38]" />
                      <span className="opacity-85">{f}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs opacity-55">
                  Service prices are starting points, subject to scope: size of code, scanning frequency and contract length (once, monthly, quarterly, annual).
                </p>
              </div>
            </Reveal>
          </aside>
        </div>
      </Section>
    </MarketingLayout>
  );
}
